import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Upload, FileText, ImageIcon, Sparkles, ArrowRight, ArrowLeft, AlertCircle, CheckCircle, Loader2, Package, MapPin, Phone, Globe, Edit2, Info } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '@/components/Logo';
import Cookies from 'js-cookie';
import { getApiUrl } from '@/config/api';
import { frictionlessTempApi } from '@/services/api/frictionlessTempApi';
import { useToast } from '@/components/ui/use-toast';
import { MAX_UPLOAD_SIZE_MB, MAX_UPLOAD_SIZE_BYTES, ALLOWED_FILE_TYPES } from '@/config/upload';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

const RestaurantMenuUploadFallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasPendingExtraction, setHasPendingExtraction] = useState(false);
  const [extractedItemsCount, setExtractedItemsCount] = useState<number>(0);
  const [isClaiming, setIsClaiming] = useState(false);
  
  // Restaurant info from search results or fallback
  const restaurant = location.state?.restaurant;
  const searchQuery = location.state?.searchQuery; // The restaurant they searched for
  const [editingField, setEditingField] = useState<string | null>(null);
  const [restaurantInfo, setRestaurantInfo] = useState({
    name: restaurant?.name || searchQuery || '',
    address: restaurant?.address || '',
    phone: restaurant?.phone || '',
    website: restaurant?.website || ''
  });

  useEffect(() => {
    // Check for session token
    const sessionToken = Cookies.get('menu_session_token');
    
    if (sessionToken) {
      // Check session status
      checkSessionStatus(sessionToken);
    } else {
      setCheckingSession(false);
    }
  }, []);

  const checkSessionStatus = async (token: string) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/frictionless-onboarding/session-status/${token}`);
      if (response.ok) {
        const data = await response.json();
        setSessionData(data);
        
        if (data.extraction_complete) {
          setHasPendingExtraction(true);
          setExtractedItemsCount(data.items_extracted || 0);
        }
      }
    } catch (error) {
      console.error('Error checking session:', error);
    } finally {
      setCheckingSession(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && ALLOWED_FILE_TYPES.includes(droppedFile.type)) {
      if (droppedFile.size > MAX_UPLOAD_SIZE_BYTES) {
        setError(`File size exceeds ${MAX_UPLOAD_SIZE_MB}MB limit`);
        return;
      }
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please upload a PDF file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
      if (selectedFile.size > MAX_UPLOAD_SIZE_BYTES) {
        setError(`File size exceeds ${MAX_UPLOAD_SIZE_MB}MB limit`);
        return;
      }
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please upload a PDF file');
    }
  };

  const handleExtractMenu = async () => {
    if (!file) return;
    
    setIsProcessing(true);
    setError('');
    
    try {
      // Create session token if not exists
      let sessionToken = Cookies.get('menu_session_token');
      if (!sessionToken) {
        sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        Cookies.set('menu_session_token', sessionToken, { expires: 1 });
      }
      
      // Upload file and extract menu
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_token', sessionToken);
      formData.append('restaurant_info', JSON.stringify(restaurantInfo));
      
      const response = await fetch(`${getApiUrl()}/api/frictionless-onboarding/upload-extract`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to extract menu');
      }
      
      const data = await response.json();
      
      // Navigate to review page
      navigate('/restaurant-menu-review', {
        state: {
          processingId: sessionToken,
          restaurant: restaurantInfo,
          itemCount: data.items_extracted
        }
      });
      
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload and process menu');
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to process your menu',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClaimExtraction = async () => {
    if (!sessionData?.session_id) return;
    
    setIsClaiming(true);
    try {
      // Navigate to review page with existing extraction
      navigate('/restaurant-menu-review', {
        state: {
          processingId: sessionData.session_id,
          restaurant: restaurantInfo,
          itemCount: extractedItemsCount
        }
      });
    } catch (error) {
      console.error('Error claiming extraction:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your extracted menu',
        variant: 'destructive'
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const handleEditField = (field: string) => {
    setEditingField(field);
  };

  const handleSaveField = (field: string, value: string) => {
    setRestaurantInfo(prev => ({
      ...prev,
      [field]: value
    }));
    setEditingField(null);
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/restaurant-menu-search')}
              className="text-gray-600"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Search Again
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Fallback Message Alert */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>No menu PDF found online for "{restaurantInfo.name || searchQuery}"</strong>
            <br />
            Don't worry! You can upload your menu PDF manually and we'll extract all the items for you.
          </AlertDescription>
        </Alert>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Upload Your Menu
            </h1>
            <p className="text-lg text-gray-600">
              We'll automatically extract all items and prices from your PDF
            </p>
          </div>

          {/* Restaurant Info Card */}
          <Card className="p-6 mb-6 bg-white">
            <h2 className="text-lg font-semibold mb-4">Restaurant Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <Package className="h-4 w-4 text-gray-400" />
                  {editingField === 'name' ? (
                    <Input
                      value={restaurantInfo.name}
                      onChange={(e) => setRestaurantInfo(prev => ({ ...prev, name: e.target.value }))}
                      onBlur={() => handleSaveField('name', restaurantInfo.name)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveField('name', restaurantInfo.name)}
                      autoFocus
                      className="flex-1"
                    />
                  ) : (
                    <>
                      <span className="text-gray-700 flex-1">{restaurantInfo.name || 'Restaurant Name'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditField('name')}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  {editingField === 'address' ? (
                    <Input
                      value={restaurantInfo.address}
                      onChange={(e) => setRestaurantInfo(prev => ({ ...prev, address: e.target.value }))}
                      onBlur={() => handleSaveField('address', restaurantInfo.address)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveField('address', restaurantInfo.address)}
                      autoFocus
                      className="flex-1"
                    />
                  ) : (
                    <>
                      <span className="text-gray-700 flex-1">{restaurantInfo.address || 'Add Address'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditField('address')}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <Phone className="h-4 w-4 text-gray-400" />
                  {editingField === 'phone' ? (
                    <Input
                      value={restaurantInfo.phone}
                      onChange={(e) => setRestaurantInfo(prev => ({ ...prev, phone: e.target.value }))}
                      onBlur={() => handleSaveField('phone', restaurantInfo.phone)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveField('phone', restaurantInfo.phone)}
                      autoFocus
                      className="flex-1"
                    />
                  ) : (
                    <>
                      <span className="text-gray-700 flex-1">{restaurantInfo.phone || 'Add Phone'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditField('phone')}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <Globe className="h-4 w-4 text-gray-400" />
                  {editingField === 'website' ? (
                    <Input
                      value={restaurantInfo.website}
                      onChange={(e) => setRestaurantInfo(prev => ({ ...prev, website: e.target.value }))}
                      onBlur={() => handleSaveField('website', restaurantInfo.website)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveField('website', restaurantInfo.website)}
                      autoFocus
                      className="flex-1"
                    />
                  ) : (
                    <>
                      <span className="text-gray-700 flex-1">{restaurantInfo.website || 'Add Website'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditField('website')}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Show pending extraction if exists */}
          {hasPendingExtraction && (
            <Card className="p-6 mb-6 bg-green-50 border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-900">Menu Already Extracted!</h3>
                    <p className="text-green-700">We found {extractedItemsCount} items from your previous upload</p>
                  </div>
                </div>
                <Button
                  onClick={handleClaimExtraction}
                  disabled={isClaiming}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isClaiming ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  Continue to Review
                </Button>
              </div>
            </Card>
          )}

          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'
            } ${file ? 'bg-green-50 border-green-500' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="space-y-4">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                <div className="text-lg font-medium text-gray-900">{file.name}</div>
                <div className="text-sm text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </div>
                <div className="flex gap-4 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFile(null);
                      setError('');
                    }}
                  >
                    Choose Different File
                  </Button>
                  <Button
                    onClick={handleExtractMenu}
                    disabled={isProcessing}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Extracting Menu...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Extract Menu Items
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="h-16 w-16 text-gray-400 mx-auto" />
                <div className="text-lg font-medium text-gray-900">
                  Drop your menu PDF here
                </div>
                <div className="text-sm text-gray-500">
                  or click to browse
                </div>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Choose PDF File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div className="text-xs text-gray-400">
                  Maximum file size: {MAX_UPLOAD_SIZE_MB}MB
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          )}

          {/* Features */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
                <Sparkles className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">AI-Powered Extraction</h3>
              <p className="text-sm text-gray-600">
                Our AI accurately extracts all menu items, prices, and descriptions
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">95% Accuracy</h3>
              <p className="text-sm text-gray-600">
                Industry-leading accuracy in menu item extraction
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
                <ImageIcon className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">AI-Generated Images</h3>
              <p className="text-sm text-gray-600">
                Get beautiful AI-generated images for each menu item
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RestaurantMenuUploadFallback;